
import React from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatCurrency, formatDate } from '../../utils';

interface PaymentDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  repayment: any | null;
}

export const PaymentDetailsDialog: React.FC<PaymentDetailsDialogProps> = ({
  open,
  onOpenChange,
  repayment
}) => {
  const handleProcessPayment = () => {
    toast.success("Payment processed successfully");
    onOpenChange(false);
    // In a real app, this would update the payment status in the database
  };

  const handleSendReminder = () => {
    toast.success("Payment reminder sent successfully");
  };

  const handleCreateErpEntry = () => {
    toast.success("ERP entry created successfully");
  };

  if (!repayment) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Payment Details</DialogTitle>
          <DialogDescription>
            Review payment details and process the transaction
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="text-muted-foreground">Borrowing:</div>
            <div className="font-medium">{repayment.borrowingName || 'Term Loan'}</div>
            
            <div className="text-muted-foreground">Date:</div>
            <div>{formatDate(repayment.date)}</div>
            
            <div className="text-muted-foreground">Principal:</div>
            <div>{formatCurrency(repayment.principal)}</div>
            
            <div className="text-muted-foreground">Interest:</div>
            <div>{formatCurrency(repayment.interest)}</div>
            
            <div className="text-muted-foreground">Total Amount:</div>
            <div className="font-medium">{formatCurrency(repayment.total)}</div>
            
            <div className="text-muted-foreground">Status:</div>
            <div>
              <Badge 
                variant={repayment.status === 'Paid' ? 'outline' : 
                        repayment.status === 'Overdue' ? 'destructive' : 'secondary'}
              >
                {repayment.status}
              </Badge>
            </div>
            
            {repayment.status === 'Paid' && (
              <>
                <div className="text-muted-foreground">Payment Date:</div>
                <div>{formatDate(repayment.paidDate || repayment.date)}</div>
                
                <div className="text-muted-foreground">Transaction Ref:</div>
                <div>{repayment.transactionRef || 'UTR12345678'}</div>
              </>
            )}
          </div>
          
          <Separator className="my-6" />
          
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Payment Workflow</h3>
            <div className="grid grid-cols-1 gap-3">
              {repayment.status !== 'Paid' ? (
                <Button 
                  onClick={handleProcessPayment}
                  className="justify-start"
                >
                  Process Payment
                </Button>
              ) : (
                <Button 
                  variant="outline" 
                  className="justify-start"
                  disabled
                >
                  Payment Complete
                </Button>
              )}
              <Button 
                variant="outline" 
                className="justify-start"
              >
                Generate Payment Voucher
              </Button>
              {repayment.status !== 'Paid' && (
                <Button 
                  variant="outline" 
                  onClick={handleSendReminder}
                  className="justify-start"
                >
                  Send Payment Reminder
                </Button>
              )}
            </div>
          </div>
          
          <Separator className="my-6" />
          
          <div className="space-y-4">
            <h3 className="text-sm font-medium">ERP Integration</h3>
            <div className="grid grid-cols-1 gap-3">
              {repayment.erpStatus ? (
                <div className="p-3 bg-muted rounded-md text-sm">
                  <div className="flex items-center text-green-600 font-medium">
                    <span className="mr-2">✓</span>
                    Posted to ERP
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Transaction ID: {repayment.erpRef || 'ERP-79024381'}
                  </div>
                </div>
              ) : (
                <Button 
                  variant="secondary" 
                  onClick={handleCreateErpEntry}
                  className="justify-start"
                >
                  Create ERP Entry
                </Button>
              )}
              
              {repayment.status !== 'Paid' && (
                <Button 
                  variant="secondary" 
                  className="justify-start"
                >
                  Schedule Auto-Payment
                </Button>
              )}
            </div>
          </div>

          <Separator className="my-6" />
          
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Related Documents</h3>
            <div className="grid grid-cols-1 gap-3">
              <Button 
                variant="outline" 
                className="justify-start"
              >
                View Loan Agreement
              </Button>
              {repayment.status === 'Paid' ? (
                <Button 
                  variant="outline" 
                  className="justify-start"
                >
                  Download Payment Receipt
                </Button>
              ) : (
                <Button 
                  variant="outline" 
                  className="justify-start"
                >
                  Payment Instructions
                </Button>
              )}
            </div>
          </div>
        </div>
        
        <DialogFooter className="sm:justify-start">
          <Button 
            variant="secondary" 
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
